import { supabase } from '../lib/supabase';
import type { Campaign, CampaignInsert, CampaignUpdate, CampaignWithComputed, CampaignFilters, DashboardStats, KpiStats, ClientPaymentStatus } from '../types/campaign';
import { statusTransitionService } from './statusTransitionService';
import { userService } from './userService';
import { activityLogService } from './activityLogService';
import { invoiceRequestService, pendingLines, type InvoiceRequest } from './invoiceRequestService';
import { invoiceService } from './invoiceService';
import { getNetPayableAmount, getNetPayableBreakdown, getPaidReceivedAmount, deriveInfluencerInvoiceStatus, hasOverdueInvoices, type PayableOverrides } from '../utils/paymentCalculations';
import { calculateInfluencerCommission, sumCreatorCommissions, sumCreatorFees, isRevenueOnlyCampaign, computeCampaignCommission, getCampaignTotalValue, getCreatorTotalDue, getCommissionableFee } from '../utils/commissionCalculations';
import type { CreatorCommissionData } from '../utils/commissionCalculations';
import { log } from '../lib/logger';
import { normalizeCampaignNames } from '../utils/text';
import { getPayableReadinessForCampaign } from '../utils/payableReadiness';
import { parseJsonArray, toJsonColumn } from '../lib/jsonColumn';
import { withMissingColumnRetry } from '../lib/pendingSchema';
import { needsClientInvoice } from '../utils/invoicePredicates';
import { daysBetween, parseDateOnly, todayStart, toLocalDateOnly } from '../utils/dates';
import { getCampaignDeductionsTotal, settleExpensesOnPayout, type ParsedCampaignExpense } from '../utils/campaignExpenses';

interface IMAggregate {
  im_total_fee: number;
  im_total_commission: number;
}

export const computeCampaignFields = (campaign: Campaign, imAgg?: IMAggregate): CampaignWithComputed => {
  const isSponsorship = (campaign as any).campaign_mode === 'sponsorship';
  const isIM = (campaign as any).campaign_mode === 'influencer_marketing';
  const sponsorshipAmount = Number((campaign as any).sponsorship_amount) || 0;
  const agencyCommissionPercent = Number((campaign as any).agency_commission_percent) || 0;

  let effective_fee = campaign.fee;
  let effective_commission_pct = campaign.commission_pct;
  const campaignCommType = (campaign as any).commission_type || 'percentage';
  const campaignCommFlat = Number((campaign as any).commission_flat_fee) || 0;
  // Commission on the Valor minus pass-through (the expenses inside the
  // Valor go to the creator untouched)
  let commission_amount = calculateInfluencerCommission(
    getCommissionableFee(campaign as any), campaign.commission_pct, campaignCommFlat, campaignCommType
  );
  let net_fee_agency = commission_amount;
  // Fee net of commission, plus pass-through reimbursed in full, minus any
  // deduction we carry for the creator — one rule shared with the payable math
  let net_fee_influencer = getCreatorTotalDue({ ...campaign, commission_amount });

  // Revenue-only service: nothing is paid out, the whole fee is our margin
  if (isRevenueOnlyCampaign(campaign as any)) {
    commission_amount = effective_fee;
    effective_commission_pct = effective_fee > 0 ? 1 : 0;
    net_fee_agency = effective_fee;
    net_fee_influencer = 0;
  } else if (isIM && imAgg) {
    effective_fee = imAgg.im_total_fee;
    commission_amount = imAgg.im_total_commission;
    effective_commission_pct = effective_fee > 0 ? commission_amount / effective_fee : 0;
    net_fee_agency = commission_amount;
    net_fee_influencer = Math.max(0, effective_fee - commission_amount - getCampaignDeductionsTotal(campaign));
  } else if (isSponsorship) {
    effective_fee = sponsorshipAmount;
    effective_commission_pct = agencyCommissionPercent;
    commission_amount = computeCampaignCommission(campaign);
    net_fee_agency = commission_amount;
    net_fee_influencer = getCreatorTotalDue({ ...campaign, commission_amount });
  }

  let days_remaining_to_due: number | null = null;
  let company_invoice_overdue_days: number | null = null;

  if (campaign.company_invoice_status === 'Issued' && campaign.company_invoice_due_date) {
    const dueDate = parseDateOnly(campaign.company_invoice_due_date);
    days_remaining_to_due = dueDate ? daysBetween(todayStart(), dueDate) : null;

    if (days_remaining_to_due !== null && days_remaining_to_due < 0) {
      company_invoice_overdue_days = Math.abs(days_remaining_to_due);
    }
  }

  const actualReceived = getPaidReceivedAmount(campaign);
  // Compare against what the client is actually invoiced, pass-through included
  const totalClientValue = isIM ? effective_fee : getCampaignTotalValue(campaign);
  let client_payment_status: ClientPaymentStatus = 'Unpaid';
  if (actualReceived >= totalClientValue && totalClientValue > 0) {
    client_payment_status = 'Paid';
  } else if (actualReceived > 0) {
    client_payment_status = 'Partially Paid';
  }

  let row_color: 'green' | 'red' | 'orange' | 'yellow' | 'grey' | null = null;

  // Sponsorships and services have no deliverables: "delivered" means the
  // campaign has reached billing, not that something was published.
  const modeStatus: string = (campaign as any).campaign_status || 'Consultation';
  const hasDeliverableWorkflow = !isSponsorship && (campaign as any).campaign_mode !== 'services';
  const deliveryDone = hasDeliverableWorkflow
    ? campaign.delivery_status === 'Published'
    : modeStatus === 'Billing' || modeStatus === 'Completed';

  const derivedInfluencerStatus = deriveInfluencerInvoiceStatus(campaign);
  const creatorSettled = derivedInfluencerStatus === 'Paid' || isRevenueOnlyCampaign(campaign as any);
  if (deliveryDone && client_payment_status === 'Paid' && creatorSettled) {
    row_color = 'green';
  } else if (campaign.company_invoice_status === 'Issued' &&
             company_invoice_overdue_days !== null &&
             client_payment_status !== 'Paid') {
    row_color = 'red';
  } else if (campaign.company_invoice_status === 'Issued' && client_payment_status !== 'Paid') {
    row_color = 'orange';
  } else if (hasDeliverableWorkflow && campaign.delivery_status === 'Delivered' && campaign.company_invoice_status !== 'Issued') {
    row_color = 'yellow';
  } else if (hasDeliverableWorkflow
      ? (campaign.delivery_status === 'Draft' || campaign.delivery_status === 'In Progress')
      : modeStatus === 'Consultation') {
    row_color = 'grey';
  }

  const dbCampaignStatus = (campaign as any).campaign_status || 'Consultation';

  // Show billing tag when in In Progress status with invoices issued
  const hasIssuedInvoices = campaign.company_invoice_status === 'Issued' ||
                            campaign.company_invoice_status === 'Overdue' ||
                            campaign.company_invoice_status === 'Paid';
  const isInProgress = dbCampaignStatus === 'In Progress';
  const showBillingTag = isInProgress && hasIssuedInvoices && campaign.delivery_status !== 'Published';

  return {
    ...campaign,
    fee: effective_fee,
    commission_pct: effective_commission_pct,
    commission_amount,
    net_fee_agency,
    net_fee_influencer,
    days_remaining_to_due,
    client_payment_status,
    company_invoice_overdue_days,
    row_color,
    campaign_status: dbCampaignStatus,
    client_confirmed: (campaign as any).client_confirmed || false,
    show_billing_tag: showBillingTag
  };
};

export async function fetchIMAggregateByCampaign(campaignIds: string[]): Promise<Map<string, IMAggregate>> {
  const result = new Map<string, IMAggregate>();
  if (campaignIds.length === 0) return result;

  const { data, error } = await supabase
    .from('campaign_influencers')
    .select('campaign_id, agreed_fee, commission_pct, commission_flat_fee, commission_type')
    .in('campaign_id', campaignIds);

  if (error || !data) return result;

  const grouped = new Map<string, CreatorCommissionData[]>();
  for (const row of data) {
    const list = grouped.get(row.campaign_id) || [];
    list.push({
      agreed_fee: Number(row.agreed_fee) || 0,
      commission_pct: Number(row.commission_pct) || 0,
      commission_flat_fee: Number(row.commission_flat_fee) || 0,
      commission_type: row.commission_type || 'percentage',
    });
    grouped.set(row.campaign_id, list);
  }

  for (const [cid, creators] of grouped) {
    result.set(cid, {
      im_total_fee: sumCreatorFees(creators),
      im_total_commission: sumCreatorCommissions(creators),
    });
  }

  return result;
}

/**
 * Multi-influencer and sponsorship campaigns keep their invoices in the
 * normalized `invoices` table; the campaign row's mohit_invoices /
 * influencer_invoices JSON is a stale snapshot those forms never update.
 * This replaces the JSON in-memory with fresh arrays built from the table so
 * every consumer (overdue KPI, payments ready, cross-check) reads live data.
 */
export async function hydrateTableBackedInvoices(campaigns: any[]): Promise<void> {
  const targets = campaigns.filter(
    (c) => c.campaign_mode === 'influencer_marketing' || c.campaign_mode === 'sponsorship'
  );
  if (targets.length === 0) return;

  try {
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select('id, campaign_id, type, number, issue_date, due_date, subtotal, total, status, counterparty_name, notes, display_order')
      .in('campaign_id', targets.map((c) => c.id))
      .order('display_order', { ascending: true });
    if (error) throw error;
    if (!invoices || invoices.length === 0) return;

    const invoiceIds = invoices.map((i) => i.id);

    const paidAtByInvoice = new Map<string, string>();
    const { data: payments } = await supabase
      .from('payments')
      .select('invoice_id, paid_at')
      .in('invoice_id', invoiceIds)
      .order('paid_at', { ascending: false });
    for (const p of payments || []) {
      if (!paidAtByInvoice.has(p.invoice_id)) {
        paidAtByInvoice.set(p.invoice_id, toLocalDateOnly(p.paid_at));
      }
    }

    const linksByPayable = new Map<string, string[]>();
    const { data: links } = await supabase
      .from('invoice_links')
      .select('client_invoice_id, payable_invoice_id')
      .in('payable_invoice_id', invoiceIds);
    for (const l of links || []) {
      const arr = linksByPayable.get(l.payable_invoice_id) || [];
      arr.push(l.client_invoice_id);
      linksByPayable.set(l.payable_invoice_id, arr);
    }

    const byCampaign = new Map<string, any[]>();
    for (const inv of invoices) {
      const arr = byCampaign.get(inv.campaign_id) || [];
      arr.push(inv);
      byCampaign.set(inv.campaign_id, arr);
    }

    for (const campaign of targets) {
      const rows = byCampaign.get(campaign.id) || [];
      if (rows.length === 0) continue; // keep the JSON when the table has nothing

      campaign.mohit_invoices = rows
        .filter((r) => r.type === 'Client')
        .map((r) => ({
          id: r.id,
          status: r.status === 'Draft' ? 'Not Issued'
            : r.status === 'Paid' ? 'Paid'
            : r.status === 'Canceled' ? 'Cancelled'
            : 'Issued',
          invoice_number: r.number || '',
          issued_date: r.issue_date || '',
          due_date: r.due_date || '',
          // Amounts are net; total carries IVA on top (see invoiceService.netToInvoiceAmounts)
          amount_received: Number(r.subtotal) || Number(r.total) || 0,
          payment_received_date: paidAtByInvoice.get(r.id) || '',
        }));

      campaign.influencer_invoices = rows
        .filter((r) => r.type === 'Influencer')
        .map((r) => ({
          id: r.id,
          invoice_number: r.number || '',
          issue_date: r.issue_date || '',
          due_date: r.due_date || '',
          amount: Number(r.subtotal) || Number(r.total) || 0,
          status: r.status === 'Paid' ? 'Paid'
            : r.status === 'Issued' ? 'Received'
            : r.notes === 'STATUS: Requested' ? 'Requested'
            : 'Not Requested',
          payment_received_date: paidAtByInvoice.get(r.id) || '',
          source_name: r.counterparty_name || '',
          funded_by_ids: linksByPayable.get(r.id) || [],
        }));
    }
  } catch (err) {
    // Non-fatal: fall back to the stored JSON snapshot
    log.warn('Failed to hydrate table-backed invoices:', err);
  }
}

/** Thrown when the campaign wasn't deleted because the user may not delete it. */
export const CAMPAIGN_DELETE_DENIED = 'CAMPAIGN_DELETE_DENIED';

export const campaignService = {
  async getAll(): Promise<CampaignWithComputed[]> {
    const userRole = await userService.getCurrentUserRole();

    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    const campaigns = data || [];

    const imAggMap = await fetchIMAggregateByCampaign(
      campaigns.filter((c: any) => c.campaign_mode === 'influencer_marketing').map((c: any) => c.id)
    );
    await hydrateTableBackedInvoices(campaigns);

    return campaigns.map((c: any) => computeCampaignFields(c, imAggMap.get(c.id)));
  },

  async getById(id: string): Promise<CampaignWithComputed | null> {
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    const imAggMap = (data as any).campaign_mode === 'influencer_marketing'
      ? await fetchIMAggregateByCampaign([data.id])
      : new Map();
    await hydrateTableBackedInvoices([data]);
    return computeCampaignFields(data, imAggMap.get(data.id));
  },

  async create(campaign: CampaignInsert): Promise<CampaignWithComputed> {
    // "EDP" and "EDP  " must not become two brands
    normalizeCampaignNames(campaign as Record<string, unknown>);
    const campaignWithStatus: any = {
      ...campaign,
      campaign_status: 'Consultation' as const
    };

    // If no manual work_code override, claim one atomically via RPC
    if (!campaignWithStatus.work_code || campaignWithStatus._autoWorkCode) {
      delete campaignWithStatus._autoWorkCode;
      const year = campaignWithStatus.year || new Date().getFullYear();
      campaignWithStatus.work_code = await this.claimNextWorkCode(year);
    }

    // A column the database does not have yet must not block the whole save
    const data = await withMissingColumnRetry(campaignWithStatus, async (payload) => {
      const { data, error } = await supabase
        .from('campaigns')
        .insert(payload)
        .select()
        .single();

      if (error) {
        log.error('Campaign creation error:', error);
        throw new Error(error.message || 'Failed to create campaign');
      }
      return data;
    });

    try {
      await activityLogService.logCampaignCreated(data.id, data.name || data.brand || 'New Campaign');
    } catch (logError) {
      log.error('Failed to log campaign creation:', logError);
    }

    return computeCampaignFields(data);
  },

  async update(id: string, campaign: CampaignUpdate): Promise<CampaignWithComputed> {
    normalizeCampaignNames(campaign as Record<string, unknown>);
    const { data: oldData } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    const campaignData: any = { ...campaign, updated_at: new Date().toISOString() };

    // If work_code is being auto-assigned on year change, claim atomically
    if (campaignData._autoWorkCode) {
      delete campaignData._autoWorkCode;
      const year = campaignData.year || new Date().getFullYear();
      campaignData.work_code = await this.claimNextWorkCode(year);
    }

    // A column the database does not have yet must not block the whole save
    const data = await withMissingColumnRetry(campaignData, async (payload) => {
      const { data, error } = await supabase
        .from('campaigns')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        log.error('Campaign update error:', error);
        throw new Error(error.message || 'Failed to update campaign');
      }
      return data;
    });

    if (oldData) {
      try {
        await activityLogService.logCampaignChanges(id, oldData, data);
      } catch (logError) {
        log.error('Failed to log campaign changes:', logError);
      }
    }

    return computeCampaignFields(data);
  },

  async delete(id: string): Promise<void> {
    // No activity entry: the log rows cascade away with the campaign.
    // A delete the policies refuse matches no row and returns no error.
    const { data: deleted, error } = await supabase
      .from('campaigns')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) throw error;
    if (!deleted || deleted.length === 0) throw new Error(CAMPAIGN_DELETE_DENIED);
  },

  async generateNextWorkCode(year: number): Promise<string> {
    const { data, error } = await supabase
      .from('campaigns')
      .select('work_code')
      .like('work_code', `${year}/%`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    let maxSequence = 0;

    if (data && data.length > 0) {
      data.forEach(item => {
        const match = item.work_code.match(/\/(\d+)/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxSequence) {
            maxSequence = num;
          }
        }
      });
    }

    const nextSequence = maxSequence + 1;
    return `${year}/${nextSequence}`;
  },

  async claimNextWorkCode(year: number): Promise<string> {
    const { data, error } = await supabase.rpc('generate_next_work_code', { p_year: year });
    if (error) throw error;
    return data;
  },

  async getUniqueClients(): Promise<string[]> {
    // Get all clients from the clients table
    const { data, error } = await supabase
      .from('clients')
      .select('name')
      .order('name');

    if (error) throw error;

    return data.map(item => item.name);
  },

  async getUniqueInfluencers(): Promise<string[]> {
    const { data, error } = await supabase
      .from('campaigns')
      .select('influencer')
      .order('influencer');

    if (error) throw error;

    const uniqueInfluencers = [...new Set(data.map(item => item.influencer))];
    return uniqueInfluencers.sort();
  },

  filterCampaigns(campaigns: CampaignWithComputed[], filters: CampaignFilters): CampaignWithComputed[] {
    return campaigns.filter(campaign => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableFields = [
          campaign.work_code,
          campaign.brand,
          campaign.influencer,
          campaign.client,
          campaign.campaign_ref,
          campaign.agent_name,
          campaign.company_invoice_number,
          campaign.notes,
          campaign.brief_description
        ];

        let found = searchableFields.some(field =>
          field?.toLowerCase().includes(searchTerm)
        );

        if (!found && campaign.mohit_invoices) {
          const invoices = parseJsonArray<{ invoice_number?: string }>(campaign.mohit_invoices);
          found = invoices.some(inv =>
            inv.invoice_number?.toLowerCase().includes(searchTerm)
          );
        }
        if (!found) return false;
      }

      if (filters.year && campaign.year !== filters.year) return false;
      if (filters.brand && campaign.brand !== filters.brand) return false;
      if (filters.influencer && campaign.influencer !== filters.influencer) return false;
      if (filters.agent_name && campaign.agent_name !== filters.agent_name) return false;
      if (filters.campaign_status && campaign.campaign_status !== filters.campaign_status) return false;
      if (filters.company_invoice_status && campaign.company_invoice_status !== filters.company_invoice_status) return false;
      if (filters.influencer_invoice_status && campaign.influencer_invoice_status !== filters.influencer_invoice_status) return false;
      if (filters.client_payment_status && campaign.client_payment_status !== filters.client_payment_status) return false;
      if (filters.delivery_status && campaign.delivery_status !== filters.delivery_status) return false;
      if (filters.invoice_number) {
        const searchTerm = filters.invoice_number.toLowerCase();
        let found = false;
        if (campaign.company_invoice_number?.toLowerCase().includes(searchTerm)) {
          found = true;
        }
        if (!found && campaign.mohit_invoices) {
          const invoices = parseJsonArray<{ invoice_number?: string }>(campaign.mohit_invoices);
          found = invoices.some(inv =>
            inv.invoice_number?.toLowerCase().includes(searchTerm)
          );
        }
        if (!found) return false;
      }
      return true;
    });
  },

  calculateDashboardStats(campaigns: CampaignWithComputed[]): DashboardStats {
    const stats: DashboardStats = {
      ongoing_campaigns: 0,
      active_clients: 0,
      active_influencers: 0,
      pending_invoices: 0
    };

    const activeInfluencers = new Set<string>();

    campaigns.forEach(campaign => {
      const campaignStatus = (campaign as any).campaign_status || 'Consultation';

      // Ongoing campaigns: campaigns with status "In Progress"
      if (campaignStatus === 'In Progress') {
        stats.ongoing_campaigns++;
        // Active Influencers: influencers with campaigns "In Progress"
        activeInfluencers.add(campaign.influencer);
      }

      // Pending invoices: campaigns that are "Completed" but have not issued invoice to client
      if (campaignStatus === 'Completed' && campaign.company_invoice_status === 'Not Issued') {
        stats.pending_invoices++;
      }
    });

    stats.active_influencers = activeInfluencers.size;

    return stats;
  },

  exportToCSV(campaigns: CampaignWithComputed[]): string {
    const headers = [
      'Year', 'WorkCode', 'Client', 'Influencer', 'Deliverables', 'DeliveryDate', 'DeliveryStatus',
      'Fee', 'CommissionPct', 'CommissionAmount', 'NetFee_Agency', 'NetFee_Influencer',
      'InvoiceStatus', 'ClientInvoiceNumber', 'InvoiceEmissionDate', 'InvoiceDueDate',
      'DaysRemainingToDue', 'ClientAmountPaid', 'ClientPaymentStatus', 'ClientPaymentDate',
      'InfluencerPaymentStatus', 'InfluencerPaymentDate', 'NetToBePaidToInfluencer', 'InfluencerInvoiceNumber'
    ];

    const rows = campaigns.map(c => [
      c.year,
      c.work_code,
      c.client,
      c.influencer,
      c.deliverables || '',
      c.delivery_date || '',
      c.delivery_status,
      c.fee,
      c.commission_pct,
      c.commission_amount,
      c.net_fee_agency,
      c.net_fee_influencer,
      c.invoice_status,
      c.client_invoice_number || '',
      c.invoice_emission_date || '',
      c.invoice_due_date || '',
      c.days_remaining_to_due || '',
      c.client_amount_paid,
      c.client_payment_status,
      c.client_payment_date || '',
      c.influencer_payment_status,
      c.influencer_payment_date || '',
      c.net_fee_influencer,
      c.influencer_invoice_number || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        const stringCell = String(cell);
        if (stringCell.includes(',') || stringCell.includes('"') || stringCell.includes('\n')) {
          return `"${stringCell.replace(/"/g, '""')}"`;
        }
        return stringCell;
      }).join(','))
    ].join('\n');

    return csvContent;
  },

  async confirmSponsor(campaignId: string): Promise<{ success: boolean; error?: string; newStatus?: string }> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .rpc('confirm_sponsor', {
        p_campaign_id: campaignId,
        p_user_id: userData.user.id
      });

    if (error) {
      log.error('Error confirming sponsor:', error);
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; error?: string; new_status?: string };

    if (result.success) {
      try {
        await activityLogService.logFieldChange(
          campaignId,
          'sponsor_confirmed',
          'false',
          'true'
        );
      } catch (logError) {
        log.error('Failed to log sponsor confirmation:', logError);
      }
    }

    return {
      success: result.success,
      error: result.error,
      newStatus: result.new_status
    };
  },

  async markInfluencerAsPaid(
    campaignId: string,
    paymentAmount: number,
    creatorName?: string
  ): Promise<CampaignWithComputed> {
    const today = new Date().toISOString().split('T')[0];

    const { data: oldData } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', campaignId)
      .maybeSingle();

    if (!oldData) {
      throw new Error('Campaign not found');
    }

    // Multi-influencer and sponsorship campaigns keep invoices in the invoices
    // table; the campaign row's JSON is a stale snapshot, so the payout must be
    // written to the table (otherwise this wrongly reports "already paid").
    const isTableBacked = (oldData as any).campaign_mode === 'influencer_marketing'
      || (oldData as any).campaign_mode === 'sponsorship';

    if (isTableBacked) {
      const { data: payables, error: payablesError } = await supabase
        .from('invoices')
        .select('id, subtotal, total, status, counterparty_name, display_order')
        .eq('campaign_id', campaignId)
        .eq('type', 'Influencer')
        .order('display_order', { ascending: true });
      if (payablesError) throw payablesError;

      let unpaid = (payables || []).filter(
        (p: any) => p.status !== 'Paid' && p.status !== 'Canceled'
      );
      // Only touch the invoices of the creator whose row was clicked
      if (creatorName) {
        const mine = unpaid.filter((p: any) => p.counterparty_name === creatorName);
        if (mine.length > 0) unpaid = mine;
      }

      if ((payables || []).length === 0) {
        throw new Error(
          'Esta campanha nao tem faturas a pagar registadas. Adiciona a fatura do criador antes de marcar como paga.'
        );
      }
      if (unpaid.length === 0) {
        throw new Error('Influencer is already marked as paid for this campaign');
      }

      // Settle oldest-first, only tranches the amount being paid fully covers
      // (it used to mark the next tranche paid even when the money ran out)
      let remaining = paymentAmount;
      for (const inv of unpaid) {
        const invoiceNet = Number((inv as any).subtotal) || Number((inv as any).total) || 0;
        const invoiceGross = Number((inv as any).total) || invoiceNet;
        if (invoiceNet > remaining + 0.5) break;
        await invoiceService.update((inv as any).id, { status: 'Paid' });
        await invoiceService.syncPaymentRecord((inv as any).id, 'Paid', invoiceGross, today);
        remaining -= invoiceNet;
        if (remaining <= 0.01) break;
      }

      await supabase
        .from('campaigns')
        .update({ influencer_payment_date: today, updated_at: new Date().toISOString() })
        .eq('id', campaignId);

      const refreshed = await campaignService.getById(campaignId);
      if (!refreshed) throw new Error('Campaign not found');
      return refreshed;
    }

    let hasUnpaidInvoices = oldData.influencer_invoice_status !== 'Paid';
    {
      const invoicesCheck = parseJsonArray<{ status?: string }>(oldData.influencer_invoices);
      if (invoicesCheck.length >= 1) {
        hasUnpaidInvoices = invoicesCheck.some(inv => inv.status !== 'Paid');
      }
    }

    // Readiness is money-based: even if every invoice on file is Paid, new
    // client money can make another payout due (the creator may invoice later).
    if (!hasUnpaidInvoices && getNetPayableAmount(oldData) <= 0) {
      throw new Error('Influencer is already marked as paid for this campaign');
    }

    let updatedInfluencerInvoices: string = toJsonColumn(oldData.influencer_invoices);
    let updatedExpenses: string | null = null;
    let derivedInfluencerStatus: string = 'Paid';
    let totalInfluencerPaid = paymentAmount;
    try {
      const invoices = parseJsonArray<{ id?: string; status?: string; amount?: number; payment_received_date?: string }>(oldData.influencer_invoices);
      if (invoices.length > 0) {
        // Tranches are what the creator invoices; deductions still to withhold
        // come off this transfer, so the money transferred plus the withheld
        // deductions is what these tranches cover.
        const withheld = getNetPayableBreakdown(oldData as any).deductionsApplied;
        const settledIds: string[] = [];
        // Pay out the tranches the client has funded, each with its own
        // amount. It used to mark only the first unpaid tranche with the whole
        // sum (XTB: #8 got 20 666,67 € and #9 stayed open).
        const readiness = getPayableReadinessForCampaign({ ...(oldData as any), influencer_invoices: invoices });
        let remaining = paymentAmount + withheld;
        let settled = 0;
        invoices.forEach((inv, i) => {
          if (readiness[i] !== 'ready') return;
          if ((inv.amount || 0) > remaining + 0.5) return;
          invoices[i] = { ...inv, status: 'Paid', payment_received_date: today };
          if (inv.id) settledIds.push(inv.id);
          remaining -= inv.amount || 0;
          settled++;
        });
        const firstUnpaid = invoices.findIndex(
          (inv: { status?: string }) => inv.status !== 'Paid'
        );
        if (settled > 0) {
          // Money left over with no tranche left to settle (rounding, or a
          // payout beyond the planned tranches): record it as its own entry
          if (remaining > 0.5 && firstUnpaid < 0) {
            const id = `temp-${crypto.randomUUID()}`;
            settledIds.push(id);
            invoices.push({
              id,
              status: 'Paid',
              payment_received_date: today,
              amount: Math.round(remaining * 100) / 100,
            } as (typeof invoices)[number]);
          }
        } else if (firstUnpaid >= 0) {
          // No tranche fully funded (e.g. part of a single payable): the
          // payout is recorded on the first open one, as before
          invoices[firstUnpaid] = {
            ...invoices[firstUnpaid],
            id: invoices[firstUnpaid].id || `temp-${crypto.randomUUID()}`,
            status: 'Paid',
            payment_received_date: today,
            amount: Math.round((paymentAmount + withheld) * 100) / 100,
          };
          settledIds.push(invoices[firstUnpaid].id as string);
        } else {
          // All existing invoices are settled — record the new payout as its
          // own entry instead of overwriting a previous one.
          const id = `temp-${crypto.randomUUID()}`;
          settledIds.push(id);
          invoices.push({
            id,
            status: 'Paid',
            payment_received_date: today,
            amount: Math.round((paymentAmount + withheld) * 100) / 100,
          } as (typeof invoices)[number]);
        }
        updatedInfluencerInvoices = toJsonColumn(invoices);

        // Mark what this payout settled, so it is never withheld or listed again
        const expenses = parseJsonArray<ParsedCampaignExpense>((oldData as any).campaign_expenses);
        if (expenses.length > 0 && settledIds.length > 0) {
          const allPaid = invoices.every((inv) => inv.status === 'Paid');
          const next = settleExpensesOnPayout(expenses, {
            withheld,
            deductionTrancheId: settledIds[0],
            closingTrancheId: allPaid ? settledIds[settledIds.length - 1] : null,
            date: today,
          });
          if (JSON.stringify(next) !== JSON.stringify(expenses)) updatedExpenses = JSON.stringify(next);
        }

        derivedInfluencerStatus = deriveInfluencerInvoiceStatus({ influencer_invoices: invoices });

        totalInfluencerPaid = invoices
          .filter((inv: { status?: string }) => inv.status === 'Paid')
          .reduce((sum: number, inv: { amount?: number }) => sum + (inv.amount || 0), 0);
      }
    } catch {
      // keep existing value if parsing fails
    }

    const updateData: Record<string, unknown> = {
      influencer_invoice_status: derivedInfluencerStatus,
      influencer_payment_date: today,
      influencer_amount_paid: totalInfluencerPaid,
      influencer_invoices: updatedInfluencerInvoices,
      ...(updatedExpenses ? { campaign_expenses: updatedExpenses } : {}),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('campaigns')
      .update(updateData)
      .eq('id', campaignId)
      .select()
      .single();

    if (error) {
      throw new Error(error.message || 'Failed to mark influencer as paid');
    }

    try {
      await activityLogService.logCampaignChanges(campaignId, oldData, data);
    } catch (logError) {
      log.error('Failed to log payment changes:', logError);
    }

    return computeCampaignFields(data);
  },

  async calculateKpiStats(year?: number): Promise<KpiStats> {
    // Select every column: naming them individually means one column that the
    // database does not have yet fails the whole query, which silently zeroed
    // every KPI card.
    let query = supabase
      .from('campaigns')
      .select('*');
    if (year) {
      query = query.eq('year', year);
    }
    const { data: campaigns, error } = await query;

    if (error) {
      log.error('Error fetching KPI data:', error);
      return {
        campaigns_active: 0,
        campaigns_consultation: 0,
        campaigns_in_progress: 0,
        invoices_not_issued: 0,
        invoices_overdue: 0,
        payments_ready: 0,
        payments_ready_amount: 0,
        total_commissions_expected: 0,
        campaigns_running: 0,
        invoice_requests_pending: 0,
        invoice_requests_amount: 0,
        invoice_request_campaign_ids: [],
        invoice_request_projectos: [],
      };
    }

    // Requested invoices are on their way: those campaigns are not "por pedir"
    let pendingRequests: InvoiceRequest[] = [];
    try {
      pendingRequests = await invoiceRequestService.listPending();
    } catch (err) {
      log.warn('Invoice requests not loaded for the KPIs:', err);
    }
    const requestedCampaignIds = new Set(pendingRequests.map(r => r.campaign_id).filter((id): id is string => !!id));

    const imCampaignIds = (campaigns || [])
      .filter((c: any) => c.campaign_mode === 'influencer_marketing')
      .map((c: any) => c.id);
    const imAggMap = await fetchIMAggregateByCampaign(imCampaignIds);
    await hydrateTableBackedInvoices(campaigns || []);

    const today = new Date();
    const stats: KpiStats = {
      campaigns_active: 0,
      campaigns_consultation: 0,
      campaigns_in_progress: 0,
      invoices_not_issued: 0,
      invoices_overdue: 0,
      payments_ready: 0,
      payments_ready_amount: 0,
      total_commissions_expected: 0,
      campaigns_running: 0,
      invoice_requests_pending: pendingRequests.length,
      invoice_requests_amount: Math.round(pendingRequests
        .flatMap(pendingLines)
        .reduce((sum, line) => sum + line.amount, 0) * 100) / 100,
      invoice_request_campaign_ids: [...requestedCampaignIds],
      invoice_request_projectos: pendingRequests
        .filter(r => r.projecto_id)
        .map(r => ({
          id: r.id,
          projecto_id: r.projecto_id!,
          label: r.campaign_name || 'Projeto',
          amount: pendingLines(r).reduce((sum, line) => sum + line.amount, 0),
        })),
    };

    campaigns.forEach((campaign) => {
      const campaignStatus = (campaign as any).campaign_status || 'Consultation';
      const imAgg = imAggMap.get((campaign as any).id);

      if (campaignStatus !== 'Consultation') {
        if ((campaign as any).campaign_mode === 'influencer_marketing' && imAgg) {
          stats.total_commissions_expected! += imAgg.im_total_commission;
        } else {
          // Same rule as the table: flat-fee commissions count, and a
          // revenue-only service contributes its whole fee.
          stats.total_commissions_expected! += computeCampaignCommission(campaign as any);
        }
      }

      if (campaignStatus === 'In Progress' || campaignStatus === 'Billing' || campaignStatus === 'Completed') {
        stats.campaigns_active++;
      }

      if (campaignStatus === 'Consultation') {
        stats.campaigns_consultation++;
      }

      if (campaignStatus === 'In Progress') {
        stats.campaigns_in_progress++;
      }

      if (campaignStatus === 'In Progress' || campaignStatus === 'Billing') {
        stats.campaigns_running!++;
      }

      if (
        needsClientInvoice({ ...(campaign as any), campaign_status: campaignStatus })
        && !requestedCampaignIds.has((campaign as any).id)
      ) {
        stats.invoices_not_issued++;
      }

      if (hasOverdueInvoices(campaign)) {
        stats.invoices_overdue++;
      }

      let payableOverrides: PayableOverrides | undefined;
      if ((campaign as any).campaign_mode === 'influencer_marketing' && imAgg) {
        payableOverrides = {
          totalNetEarnings: imAgg.im_total_fee - imAgg.im_total_commission,
          commissionRate: imAgg.im_total_fee > 0 ? imAgg.im_total_commission / imAgg.im_total_fee : 0,
        };
      }
      const netPayable = getNetPayableAmount(campaign as any, payableOverrides);
      if (netPayable > 0) {
        stats.payments_ready++;
        stats.payments_ready_amount! += netPayable;
      }
    });

    return stats;
  }
};
